import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { useWizardStore } from '@/store/webinar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { CurrencyInput } from '../../oferta/CurrencyInput';
import { ImageUploadField } from '../../oferta/ImageUploadField';
import { ContrastChecker } from '../../oferta/ContrastChecker';
import { BadgeSelector } from '../../oferta/BadgeSelector';
import { OfferPreview } from '../../oferta/OfferPreview';
import { ColorPicker } from '@/components/ui/color-picker';
import { TimePickerSeconds } from '../../oferta/TimePickerSeconds';
import { useOfferValidation } from '@/hooks/useOfferValidation';

export function OfertaStep() {
  useLanguage();
  const { data, updateNestedData } = useWizardStore();
  const { errors } = useOfferValidation(data.offer);

  const updateOffer = (field: string, value: any) => {
    updateNestedData('offer', field, value);
  };

  const getError = (field: string) => errors.find(e => e.field === field);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[60%_40%] gap-8">
      {/* Coluna Esquerda: Formulário */}
      <div className="space-y-6">
        
        {/* Informações Básicas */}
        <Card>
          <CardHeader>
            <CardTitle>{translateInterfaceText("Informações Básicas")}</CardTitle>
            <CardDescription>{translateInterfaceText("Defina título e identificação da oferta")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="internal_name">
                {translateInterfaceText("Nome interno")}<span className="text-xs text-muted-foreground ml-2">{translateInterfaceText("(Visível só para você)")}</span>
              </Label>
              <Input
                id="internal_name"
                value={data.offer.internal_name || ''}
                onChange={(e) => updateOffer('internal_name', e.target.value)}
                placeholder={translateInterfaceText("Ex: Oferta Black Friday 2024")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">
                {translateInterfaceText("Título da oferta")}{" "}<span className="text-destructive">*</span>
              </Label>
              <Input
                id="title"
                value={data.offer.title || ''}
                onChange={(e) => updateOffer('title', e.target.value)}
                placeholder={translateInterfaceText("Oferta relâmpago")}
                maxLength={60}
                className={getError('title') ? 'border-destructive' : ''}
              />
              {getError('title') && (
                <p className="text-xs text-destructive">{getError('title')?.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="subtitle">{translateInterfaceText("Subtítulo (opcional)")}</Label>
              <Input
                id="subtitle"
                value={data.offer.subtitle || ''}
                onChange={(e) => updateOffer('subtitle', e.target.value)}
                placeholder={translateInterfaceText("Válida enquanto durar o evento")}
                maxLength={100}
              />
            </div>
          </CardContent>
        </Card>

        {/* Preços */}
        <Card>
          <CardHeader>
            <CardTitle>{translateInterfaceText("Preços")}</CardTitle>
            <CardDescription>{translateInterfaceText("Configure os valores da oferta")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>{translateInterfaceText("Preço original (opcional)")}</Label>
              <CurrencyInput
                value={data.offer.price_original}
                onChange={(value) => updateOffer('price_original', value)}
                placeholder="0,00"
              />
            </div>

            <div className="space-y-2">
              <Label>{translateInterfaceText("Preço com desconto (opcional)")}</Label>
              <CurrencyInput
                value={data.offer.price_offer}
                onChange={(value) => updateOffer('price_offer', value)}
                placeholder="0,00"
                className={getError('price_offer') ? 'border-destructive' : ''}
              />
              {getError('price_offer') && (
                <p className="text-xs text-destructive">{getError('price_offer')?.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* CTA Principal */}
        <Card>
          <CardHeader>
            <CardTitle>{translateInterfaceText("Botão Principal (CTA)")}</CardTitle>
            <CardDescription>{translateInterfaceText("Configure o call-to-action principal")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cta1_label">
                {translateInterfaceText("Texto do botão")}{" "}<span className="text-destructive">*</span>
              </Label>
              <Input
                id="cta1_label"
                value={data.offer.cta1_label || ''}
                onChange={(e) => updateOffer('cta1_label', e.target.value)}
                placeholder={translateInterfaceText("Garantir minha vaga")}
                className={getError('cta1_label') ? 'border-destructive' : ''}
              />
              {getError('cta1_label') && (
                <p className="text-xs text-destructive">{getError('cta1_label')?.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="cta1_url">
                {translateInterfaceText("Link do botão")}{" "}<span className="text-destructive">*</span>
              </Label>
              <Input
                id="cta1_url"
                type="url"
                value={data.offer.cta1_url || ''}
                onChange={(e) => updateOffer('cta1_url', e.target.value)}
                placeholder="https://seu-checkout.com/..."
                className={getError('cta1_url') ? 'border-destructive' : ''}
              />
              {getError('cta1_url') && (
                <p className="text-xs text-destructive">{getError('cta1_url')?.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>{translateInterfaceText("Abrir link")}</Label>
              <RadioGroup
                value={data.offer.cta1_target || 'new'}
                onValueChange={(value) => updateOffer('cta1_target', value)}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="same" id="same" />
                  <Label htmlFor="same" className="font-normal cursor-pointer">
                    {translateInterfaceText("Na mesma aba")}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="new" id="new" />
                  <Label htmlFor="new" className="font-normal cursor-pointer">
                    {translateInterfaceText("Nova aba")}</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label>{translateInterfaceText("Cor do botão")}</Label>
              <ColorPicker
                color={data.offer.cta1_color || '#6366f1'}
                onChange={(color) => updateOffer('cta1_color', color)}
              />
              <ContrastChecker 
                bgColor={data.offer.cta1_color || '#6366f1'}
                fgColor="#ffffff"
              />
            </div>
          </CardContent>
        </Card>

        {/* CTA Secundário */}
        <Card>
          <CardHeader>
            <CardTitle>{translateInterfaceText("Segundo Botão (Opcional)")}</CardTitle>
            <CardDescription>{translateInterfaceText("Adicione um botão secundário, como WhatsApp")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="cta2_enabled">{translateInterfaceText("Ativar segundo botão")}</Label>
              <Switch
                id="cta2_enabled"
                checked={data.offer.cta2_enabled || false}
                onCheckedChange={(checked) => updateOffer('cta2_enabled', checked)}
              />
            </div>

            {data.offer.cta2_enabled && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="cta2_label">{translateInterfaceText("Texto do segundo botão")}</Label>
                  <Input
                    id="cta2_label"
                    value={data.offer.cta2_label || ''}
                    onChange={(e) => updateOffer('cta2_label', e.target.value)}
                    placeholder={translateInterfaceText("Tirar dúvidas no WhatsApp")}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cta2_url">{translateInterfaceText("Link do segundo botão")}</Label>
                  <Input
                    id="cta2_url"
                    type="url"
                    value={data.offer.cta2_url || ''}
                    onChange={(e) => updateOffer('cta2_url', e.target.value)}
                    placeholder="https://wa.me/..."
                    className={getError('cta2_url') ? 'border-destructive' : ''}
                  />
                  {getError('cta2_url') && (
                    <p className="text-xs text-destructive">{getError('cta2_url')?.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cta2_style">{translateInterfaceText("Estilo do botão")}</Label>
                  <Select
                    value={data.offer.cta2_style || 'outline'}
                    onValueChange={(value) => updateOffer('cta2_style', value)}
                  >
                    <SelectTrigger id="cta2_style">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="outline">{translateInterfaceText("Contornado")}</SelectItem>
                      <SelectItem value="ghost">{translateInterfaceText("Fantasma")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Imagens */}
        <Card>
          <CardHeader>
            <CardTitle>{translateInterfaceText("Imagens da Oferta")}</CardTitle>
            <CardDescription>{translateInterfaceText("Adicione imagens específicas para desktop e mobile")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <ImageUploadField
              label={translateInterfaceText("Imagem Desktop")}
              recommendedSize="563×328 px"
              value={data.offer.img_desktop}
              onChange={(url) => updateOffer('img_desktop', url)}
            />
            
            <ImageUploadField
              label={translateInterfaceText("Imagem Mobile")}
              recommendedSize="384×110 px"
              value={data.offer.img_mobile}
              onChange={(url) => updateOffer('img_mobile', url)}
            />
          </CardContent>
        </Card>

        {/* Timing */}
        <Card>
          <CardHeader>
            <CardTitle>{translateInterfaceText("Sincronização com o Vídeo")}</CardTitle>
            <CardDescription>{translateInterfaceText("Defina quando a oferta será exibida")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>{translateInterfaceText("Marcador do pitch (opcional)")}</Label>
              <TimePickerSeconds
                value={data.offer.pitch_start_sec}
                onChange={(value) => updateOffer('pitch_start_sec', value)}
                placeholder={translateInterfaceText("mm:ss")}
              />
              <p className="text-xs text-muted-foreground">
                {translateInterfaceText("Referência visual de onde começa o pitch da oferta")}</p>
            </div>

            <div className="space-y-2">
              <Label>
                {translateInterfaceText("Início da oferta")}{" "}<span className="text-destructive">*</span>
              </Label>
              <TimePickerSeconds
                value={data.offer.offer_start_sec}
                onChange={(value) => updateOffer('offer_start_sec', value)}
                placeholder={translateInterfaceText("mm:ss")}
              />
              {getError('offer_start_sec') && (
                <Alert variant="destructive" className="mt-2">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{getError('offer_start_sec')?.message}</AlertDescription>
                </Alert>
              )}
            </div>

            <div className="space-y-2">
              <Label>{translateInterfaceText("Fim da oferta (opcional)")}</Label>
              <TimePickerSeconds
                value={data.offer.offer_end_sec}
                onChange={(value) => updateOffer('offer_end_sec', value)}
                placeholder={translateInterfaceText("mm:ss")}
              />
              {getError('offer_end_sec') && (
                <p className="text-xs text-destructive">{getError('offer_end_sec')?.message}</p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="reshow_at_end">{translateInterfaceText("Reexibir no final do vídeo")}</Label>
                <p className="text-xs text-muted-foreground">
                  {translateInterfaceText("Mostrar novamente nos últimos 20 segundos")}</p>
              </div>
              <Switch
                id="reshow_at_end"
                checked={data.offer.reshow_at_end || false}
                onCheckedChange={(checked) => updateOffer('reshow_at_end', checked)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Timer e Urgência */}
        <Card>
          <CardHeader>
            <CardTitle>{translateInterfaceText("Timer e Urgência")}</CardTitle>
            <CardDescription>{translateInterfaceText("Adicione elementos de escassez")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="timer_enabled">{translateInterfaceText("Exibir contador regressivo")}</Label>
              <Switch
                id="timer_enabled"
                checked={data.offer.timer_enabled || false}
                onCheckedChange={(checked) => updateOffer('timer_enabled', checked)}
              />
            </div>

            {data.offer.timer_enabled && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="timer_label">{translateInterfaceText("Texto do timer")}</Label>
                  <Input
                    id="timer_label"
                    value={data.offer.timer_label || 'Termina em:'}
                    onChange={(e) => updateOffer('timer_label', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="timer_action">{translateInterfaceText("Ação quando zerar")}</Label>
                  <Select
                    value={data.offer.timer_action || 'keep'}
                    onValueChange={(value) => updateOffer('timer_action', value)}
                  >
                    <SelectTrigger id="timer_action">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="keep">{translateInterfaceText("Manter modal")}</SelectItem>
                      <SelectItem value="hide">{translateInterfaceText("Ocultar modal")}</SelectItem>
                      <SelectItem value="disable_cta">{translateInterfaceText("Desativar CTA")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="pt-4 border-t">
              <Label className="mb-3 block">{translateInterfaceText("Badges de urgência")}</Label>
              <BadgeSelector
                badges={data.offer.badges || { limited_spots: false, today_only: false, bonus_time: false }}
                onChange={(badges) => updateOffer('badges', badges)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Comportamento do Modal */}
        <Card>
          <CardHeader>
            <CardTitle>{translateInterfaceText("Comportamento do Modal")}</CardTitle>
            <CardDescription>{translateInterfaceText("Configure como o modal se comporta")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>{translateInterfaceText("Posição do modal")}</Label>
              <RadioGroup
                value={data.offer.modal_position || 'center'}
                onValueChange={(value) => updateOffer('modal_position', value)}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="center" id="center" />
                  <Label htmlFor="center" className="font-normal cursor-pointer">
                    {translateInterfaceText("Central")}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="slide_right" id="slide_right" />
                  <Label htmlFor="slide_right" className="font-normal cursor-pointer">
                    {translateInterfaceText("Slide direito")}</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="modal_allow_close">{translateInterfaceText("Permitir fechar no \"X\"")}</Label>
              <Switch
                id="modal_allow_close"
                checked={data.offer.modal_allow_close !== false}
                onCheckedChange={(checked) => updateOffer('modal_allow_close', checked)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="modal_close_delay">
                {translateInterfaceText("Bloquear fechamento (segundos)")}</Label>
              <div className="flex items-center gap-4">
                <Slider
                  id="modal_close_delay"
                  min={0}
                  max={30}
                  step={1}
                  value={[data.offer.modal_close_delay_sec || 0]}
                  onValueChange={([value]) => updateOffer('modal_close_delay_sec', value)}
                  className="flex-1"
                />
                <span className="text-sm text-muted-foreground w-8">
                  {data.offer.modal_close_delay_sec || 0}s
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="modal_sticky_bar">{translateInterfaceText("Persistir como barra fixa")}</Label>
                <p className="text-xs text-muted-foreground">
                  {translateInterfaceText("Mostrar barra na parte inferior após fechar")}</p>
              </div>
              <Switch
                id="modal_sticky_bar"
                checked={data.offer.modal_sticky_bar || false}
                onCheckedChange={(checked) => updateOffer('modal_sticky_bar', checked)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Integrações com Chat */}
        <Card>
          <CardHeader>
            <CardTitle>{translateInterfaceText("Integrações")}</CardTitle>
            <CardDescription>{translateInterfaceText("Conecte com chat e notificações de vendas")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="chat_auto_message">{translateInterfaceText("Publicar no chat")}</Label>
                <p className="text-xs text-muted-foreground">
                  {translateInterfaceText("Enviar mensagem automática quando a oferta abrir")}</p>
              </div>
              <Switch
                id="chat_auto_message"
                checked={data.offer.chat_auto_message || false}
                onCheckedChange={(checked) => updateOffer('chat_auto_message', checked)}
              />
            </div>

            {data.offer.chat_auto_message && (
              <div className="space-y-2">
                <Label htmlFor="chat_message_text">{translateInterfaceText("Mensagem do chat")}</Label>
                <Textarea
                  id="chat_message_text"
                  value={data.offer.chat_message_text || ''}
                  onChange={(e) => updateOffer('chat_message_text', e.target.value)}
                  rows={2}
                  maxLength={200}
                />
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="trigger_sales">{translateInterfaceText("Acionar vendas simuladas")}</Label>
                <p className="text-xs text-muted-foreground">
                  {translateInterfaceText("Se configurado na etapa \"Vendas\"")}</p>
              </div>
              <Switch
                id="trigger_sales"
                checked={data.offer.trigger_sales_simulation || false}
                onCheckedChange={(checked) => updateOffer('trigger_sales_simulation', checked)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Recursos Extras */}
        <Card>
          <CardHeader>
            <CardTitle>{translateInterfaceText("Recursos Extras")}</CardTitle>
            <CardDescription>{translateInterfaceText("Cupom e sorteio (visual apenas no MVP)")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="coupon_enabled">{translateInterfaceText("Campo de cupom")}</Label>
                <p className="text-xs text-muted-foreground">
                  {translateInterfaceText("Visual apenas • Sem validação")}</p>
              </div>
              <Switch
                id="coupon_enabled"
                checked={data.offer.coupon_enabled || false}
                onCheckedChange={(checked) => updateOffer('coupon_enabled', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="raffle_enabled">{translateInterfaceText("Habilitar sorteio")}</Label>
                <p className="text-xs text-muted-foreground">
                  {translateInterfaceText("Visual apenas • Sem lógica de sorteio")}</p>
              </div>
              <Switch
                id="raffle_enabled"
                checked={data.offer.raffle_enabled || false}
                onCheckedChange={(checked) => updateOffer('raffle_enabled', checked)}
              />
            </div>

            {data.offer.raffle_enabled && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="raffle_title">{translateInterfaceText("Título do sorteio")}</Label>
                  <Input
                    id="raffle_title"
                    value={data.offer.raffle_title || ''}
                    onChange={(e) => updateOffer('raffle_title', e.target.value)}
                    placeholder={translateInterfaceText("Ex: Concorra a um iPhone 15")}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="raffle_description">{translateInterfaceText("Descrição")}</Label>
                  <Textarea
                    id="raffle_description"
                    value={data.offer.raffle_description || ''}
                    onChange={(e) => updateOffer('raffle_description', e.target.value)}
                    placeholder={translateInterfaceText("Todos que comprarem hoje participam...")}
                    rows={2}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Coluna Direita: Preview */}
      <div className="lg:sticky lg:top-24 lg:h-fit">
        <OfferPreview data={data.offer} />
      </div>
    </div>
  );
}
